import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import Img from "gatsby-image"
import PropTypes from "prop-types"
import React from "react"
import { useInstagramPosts } from "services/graphQl/queries/instagramPosts"
import { toParagraphs } from "utils/plainText"
import "./section-social-follow-v2.scss"

const ICONS = {
  TikTok: (
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  ),
  YouTube: (
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  ),
  Instagram: (
    <>
      <rect
        fill="none"
        height="18"
        rx="5"
        stroke="currentColor"
        strokeWidth="2.2"
        width="18"
        x="3"
        y="3"
      />
      <circle
        cx="12"
        cy="12"
        fill="none"
        r="4.2"
        stroke="currentColor"
        strokeWidth="2.2"
      />
      <circle cx="17.4" cy="6.6" r="1.4" />
    </>
  ),
  Facebook: (
    <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.1 1.5-4.1 4.2v2.3H7.4V14h2.8v8h3.3z" />
  ),
  LinkedIn: (
    <>
      <circle cx="5" cy="5" r="2.3" />
      <rect height="12" width="4.2" x="2.9" y="8.5" />
      <path d="M9.6 8.5h4v1.7c.6-1 1.9-2 3.9-2 4.1 0 4.5 2.7 4.5 6.1v6.2h-4.2v-5.5c0-1.4 0-3.1-1.9-3.1s-2.2 1.5-2.2 3v5.6H9.6z" />
    </>
  ),
}

const IconLinks = ({ links }) => (
  <ul className="section-social-follow-v2--links">
    {links.map(([name, href]) => (
      <li key={name}>
        <a
          aria-label={`${name} (opens in a new tab)`}
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <svg
            aria-hidden="true"
            fill="currentColor"
            height="20"
            viewBox="0 0 24 24"
            width="20"
          >
            {ICONS[name]}
          </svg>
        </a>
      </li>
    ))}
  </ul>
)

IconLinks.propTypes = {
  links: PropTypes.arrayOf(PropTypes.array).isRequired,
}

// One Instagram post from the feed (see useInstagramPosts), with its image
// saved at build time.
const InstagramPost = ({ caption, image, imageUrl, mediaType, permalink }) => (
  <li className="section-social-follow-v2--post">
    <a
      aria-label={`${
        caption ? caption.slice(0, 80) : "Instagram post"
      } (opens Instagram in a new tab)`}
      href={permalink}
      rel="noopener noreferrer"
      target="_blank"
    >
      {image?.fluid ? (
        <Img alt="" fluid={image.fluid} />
      ) : (
        <img alt="" loading="lazy" src={imageUrl} />
      )}
      {mediaType === "VIDEO" && (
        <span
          aria-hidden="true"
          className="section-social-follow-v2--post-badge"
        >
          <svg height="16" viewBox="0 0 24 24" width="16">
            <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
          </svg>
        </span>
      )}
      {caption && (
        <span
          aria-hidden="true"
          className="section-social-follow-v2--post-caption"
        >
          {caption}
        </span>
      )}
    </a>
  </li>
)

InstagramPost.propTypes = {
  caption: PropTypes.string,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  mediaType: PropTypes.string,
  permalink: PropTypes.string,
}

const SectionSocialFollowV2 = ({
  facebookLink,
  image,
  instagramHandle,
  instagramLink,
  linkedinLink,
  showInstagramFeed,
  text,
  tiktokLink,
  title,
  youtubeLink,
}) => {
  const feed = useInstagramPosts()
  const posts = showInstagramFeed ? feed : []
  const links = [
    ["TikTok", tiktokLink],
    ["YouTube", youtubeLink],
    ["Instagram", instagramLink],
    ["Facebook", facebookLink],
    ["LinkedIn", linkedinLink],
  ].filter(([, href]) => href)

  // With Instagram posts, show them as a feed and keep the icons for the
  // other platforms. Without posts (for example if the Instagram connection
  // lapses), fall back to the image and all the icons.
  if (posts.length > 0) {
    return (
      <section className="section-social-follow-v2 section-social-follow-v2--feed">
        <div className="container-v2">
          <div className="section-social-follow-v2--feed-header">
            <div>
              {title && (
                <h2 className="section-social-follow-v2--title">{title}</h2>
              )}
              {toParagraphs(text?.text).map(paragraph => (
                <p className="section-social-follow-v2--text" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>
            {instagramLink && (
              <p className="section-social-follow-v2--follow">
                <ButtonV2
                  className={getButtonClasses("Solid Blue", "small")}
                  href={instagramLink}
                  newWindow={true}
                  text={
                    instagramHandle
                      ? `Follow ${instagramHandle}`
                      : "Follow on Instagram"
                  }
                />
              </p>
            )}
          </div>
          <ul className="section-social-follow-v2--posts">
            {posts.slice(0, 6).map(post => (
              <InstagramPost key={post.id} {...post} />
            ))}
          </ul>
          {links.length > 1 && (
            <div className="section-social-follow-v2--more">
              <p className="section-social-follow-v2--more-label">
                More places to follow
              </p>
              <IconLinks
                links={links.filter(([name]) => name !== "Instagram")}
              />
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <section className="section-social-follow-v2">
      <div className="container-v2">
        {image?.fluid && (
          <div className="section-social-follow-v2--image">
            <Img alt={image.description || ""} fluid={image.fluid} />
          </div>
        )}
        <div className="section-social-follow-v2--content">
          {title && (
            <h2 className="section-social-follow-v2--title">{title}</h2>
          )}
          {toParagraphs(text?.text).map(paragraph => (
            <p className="section-social-follow-v2--text" key={paragraph}>
              {paragraph}
            </p>
          ))}
          {links.length > 0 && <IconLinks links={links} />}
        </div>
      </div>
    </section>
  )
}

SectionSocialFollowV2.propTypes = {
  facebookLink: PropTypes.string,
  image: PropTypes.object,
  instagramHandle: PropTypes.string,
  instagramLink: PropTypes.string,
  linkedinLink: PropTypes.string,
  showInstagramFeed: PropTypes.bool,
  text: PropTypes.shape({ text: PropTypes.string }),
  tiktokLink: PropTypes.string,
  title: PropTypes.string,
  youtubeLink: PropTypes.string,
}

export default SectionSocialFollowV2
